import { computed, makeObservable, runInAction } from 'mobx';

import { moment } from '@feathr/hooks';
import type { Attributes, IRachisMessage, TConstraints, TOptionalConstraint } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import { ECollectionClassName } from '../model';
import type { ITemplate, Template } from '../templates';
import { TemplateClass } from '../templates';
import type { Campaign } from './campaign';
import { mailingConstraints } from './mailing';
import { SingularEmailBaseCampaign } from './singular_email_base';
import type {
  IAudienceSegmentIds,
  IMailrailAutoSendEmailCampaign,
  IMailrailBaseEmailCampaign,
} from './types';
import { CampaignClass, CampaignState } from './types';

type PartialAttrs = Partial<IMailrailBaseEmailCampaign>;

export abstract class MailrailBaseEmailCampaign<
  T extends IMailrailBaseEmailCampaign = IMailrailBaseEmailCampaign,
> extends SingularEmailBaseCampaign<T> {
  public override get constraints(): TConstraints<IMailrailBaseEmailCampaign> {
    return {
      ...super.constraints,
      ...mailingConstraints,
      date_start: {
        datetime: (value: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (!value) {
            return undefined;
          }
          if (attributes.state === CampaignState.Draft) {
            return {
              earliest: moment.utc().add(15, 'minutes').startOf('minute'),
              message: `^Start time must be at least 15 minutes in the future`,
            };
          }
          return undefined;
        },
      },
      name: {
        presence: {
          allowEmpty: false,
        },
      },
      segments_excluded: {
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
      segments_included: {
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
    };
  }

  constructor(attributes: Partial<T>) {
    super(attributes);

    makeObservable(this);
  }

  // TODO: Change this to a non-async void method once the base/pinpoint implementations are removed
  // This version doesn't save the campaign so there's no reason for it to be async or to return the campaign
  public override async setTemplate(template: Template): Promise<this> {
    if (!template.id) {
      throw new Error('Template is missing an id');
    }

    if (template.get('_cls') !== TemplateClass.SESEmail) {
      throw new Error('Template is not an Email Template');
    }

    this.set({ template_id: template.id } as Partial<Attributes<this>>);
    return this;
  }

  // TODO: Remove this method once the base/pinpoint implementation is removed
  // Calls to this could just be calls to `.set`, but for now we need to override the base implementation that calls save()
  public override removeTemplate(): void {
    this.set({ template_id: undefined } as Partial<Attributes<this>>);
  }

  /**
   * Overrides the base test send to support save-less testing: any unsaved (dirty) campaign and
   * template changes ride along in the request body so the backend can patch them in memory and
   * run the test synchronously, without the user having to save first. This avoids conflicting
   * with the rule against saving edits to stopped/completed Mailrail autosends/drips except when
   * re-publishing. Only changed fields are sent with the test request (see `Model.toDirtyJS`).
   *
   * When `mailingId` is provided, it is sent as `mailing_id` so the backend resolves and tests that
   * specific mailing (such as an embedded drip step) rather than the campaign's own email; omit it
   * to test the campaign itself.
   */
  public override async sendTestEmail(request: {
    recipient_emails: string[];
    recipient_id: string;
    template?: Template;
    mailingId?: string;
  }): Promise<void> {
    this.assertCollection(this.collection);
    this.assertId(this.id, ECollectionClassName.EmailBaseCampaign);

    const { recipient_emails, recipient_id, template, mailingId } = request;

    const body: {
      mailing_id?: string;
      recipient_emails: string[];
      recipient_id: string;
      campaign?: Partial<T>;
      template?: Partial<ITemplate>;
    } = { recipient_emails, recipient_id };
    if (mailingId) {
      body.mailing_id = mailingId;
    }
    if (this.isDirty) {
      body.campaign = this.toDirtyJS();
    }
    if (template?.isDirty) {
      body.template = template.toDirtyJS();
    }

    const url = `${BLACKBOX_URL}flight_campaigns/${this.id}/test`;
    const headers = this.collection.getHeaders();
    const response = await wretch<IRachisMessage>(url, {
      headers,
      body: JSON.stringify(body),
      method: 'POST',
    });
    if (isWretchError(response)) {
      runInAction(() => {
        this.isUpdating = false;
        this.isErrored = true;
        this.error = response.error;
      });
      throw response.error;
    } else {
      runInAction(() => {
        this.isUpdating = false;
        this.isErrored = false;
        this.error = null;
      });
    }
  }

  @computed
  public get isEditable(): boolean {
    return this.isDraft || this.isScheduled;
  }

  @computed
  public get hasEnded(): boolean {
    return this.isPastEndDate && !this.isDraft;
  }

  @computed
  public get hasStarted(): boolean {
    return this.isPastStartDate && !this.isDraft;
  }

  public override get audienceSegmentIds(): IAudienceSegmentIds {
    const attributes = this.attributes as IMailrailBaseEmailCampaign;
    return {
      excludedSegmentIds: (attributes.segments_excluded ?? []).filter(Boolean),
      includedSegmentIds: (attributes.segments_included ?? []).filter(Boolean),
    };
  }

  public override get includesAdditionalEmails(): boolean {
    return this.get('target_address_type') === 'all';
  }

  @computed
  public get sendsBulkAtStart(): boolean {
    if (this.isMailrailSingleSend) {
      return true;
    }
    if (this.isMailrailTriggerable) {
      // Read the bulk fields, which live on the auto-send/drip attribute shape (drip extends it).
      const attributes = this.toJS() as Partial<IMailrailAutoSendEmailCampaign>;
      return Boolean(attributes.bulk_send_at_start);
    }
    return false;
  }

  public override async stop(): Promise<MailrailBaseEmailCampaign> {
    if (!(this.isScheduled || this.isPublished)) {
      return this;
    }
    const new_state = this.isPublished ? CampaignState.Stopped : CampaignState.Draft;
    const patch = { state: new_state } as Partial<Attributes<this>>;
    this.set(patch);
    return this.patch(patch);
  }

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      date_start: undefined,
      segments_excluded: [],
      segments_included: [],
      target_address_type: 'primary',
    } as Partial<T>;
  }
}

// Function that allows type narrowing for Email Campaigns.
export function isMailrailCampaign(campaign: Campaign): campaign is MailrailBaseEmailCampaign {
  return [
    CampaignClass.MailrailAutoSend,
    CampaignClass.MailrailDrip,
    CampaignClass.MailrailSingleSend,
    CampaignClass.MailrailPartnerMessage,
  ].includes(campaign.get('_cls'));
}
